/*
 * Send internal collection links straight to the existing in-stock view.
 * This changes hrefs only: native navigation, sorting and the collection
 * provider's direct-entry/filter/pagination safeguards remain in charge.
 */
(() => {
    'use strict';
    if (window.Shopify?.designMode) return;

    const availability = 'filter.v.availability';
    const excludedHandles = new Set(['coming-soon', 'paicar', 'vendors', 'types']);
    const collectionPath = /^(?:\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*)?\/collections\/([^/]+)(?:\/([^/]+))?\/?$/i;
    const discountPath = /^(?:\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*)?\/discount\/[^/]+\/?$/i;

    function normalizeCollection(url) {
        if (url.origin !== window.location.origin || url.username || url.password) return false;
        const match = url.pathname.match(collectionPath);
        if (!match || match[2] === 'products') return false;
        const handle = decodeURIComponent(match[1]).toLowerCase();
        if (excludedHandles.has(handle) || /[/?#]/.test(handle)) return false;
        // Alternate templates and section-rendering requests are not ordinary navigation.
        if (['view', 'section_id', 'sections'].some(key => url.searchParams.has(key))) return false;
        const values = url.searchParams.getAll(availability);
        if (values.length === 1 && values[0] === '1') return false;
        // Leave unfiltered/conflicting pagination to the existing provider, which
        // knows whether this collection has native availability filtering enabled.
        if (url.searchParams.has('page')) return false;
        url.searchParams.set(availability, '1');
        return true;
    }

    function normalizedHref(href) {
        if (!href || href.trim().startsWith('#')) return null;
        try {
            const url = new URL(href, document.baseURI);
            if (url.origin !== window.location.origin || url.username || url.password) return null;
            if (normalizeCollection(url)) return url.href;
            if (!discountPath.test(url.pathname) || !url.searchParams.has('redirect')) return null;
            const redirect = new URL(url.searchParams.get('redirect'), window.location.origin);
            if (!normalizeCollection(redirect)) return null;
            // Keep the discount code and its other parameters; change only its local destination.
            url.searchParams.set('redirect', redirect.pathname + redirect.search + redirect.hash);
            return url.href;
        } catch (_) {
            return null;
        }
    }

    function normalizeAnchor(anchor) {
        if (!anchor || anchor.hasAttribute('download') || anchor.hasAttribute('data-tds-keep-url')) return;
        const href = normalizedHref(anchor.getAttribute('href'));
        if (href) anchor.setAttribute('href', href);
    }

    function normalizeInteraction(event) {
        const target = event.target?.closest ? event.target : event.target?.parentElement;
        normalizeAnchor(target?.closest('a[href]'));
    }

    // Covers server-rendered desktop/mobile menus, cards, hero buttons and footers.
    function normalizeInitialLinks() {
        document.querySelectorAll('a[href]').forEach(normalizeAnchor);
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', normalizeInitialLinks, { once: true });
    } else {
        normalizeInitialLinks();
    }

    // Also cover links inserted by drawers/AJAX, without a mutation observer,
    // navigation interception, network requests, or delay timers.
    ['pointerdown', 'focusin', 'click', 'auxclick', 'contextmenu'].forEach(type => {
        document.addEventListener(type, normalizeInteraction, { capture: true, passive: true });
    });
})();
